import React, { useState, useCallback } from "react";
import { Loader2, CheckCircle2, Camera } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ScanBar, PickPill, PICK_STATES, EXCEPTION_STATES, EXCEPTION_REASONS, Card, EmptyState } from "./fxui";
import { printHtml, pickSheetHtml } from "./print";
import { Printer } from "lucide-react";

export default function FxPicking() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excFor, setExcFor] = useState(null);

  const open = useCallback(async (code) => {
    setLoading(true); setOrder(null);
    try {
      const { data: scan } = await api.get("/fx/scan", { params: { code } });
      const onum = scan.order_number || code;
      const { data } = await api.get(`/fx/pick/${onum}`);
      setOrder(data);
    } catch { toast.error("Order not found"); } finally { setLoading(false); }
  }, []);

  const reload = async () => {
    const { data } = await api.get(`/fx/pick/${order.order_number}`);
    setOrder(data);
  };

  const mark = async (key, pick_status, extra = {}) => {
    try {
      await api.post(`/fx/pick/${order.order_number}/item`, { key, pick_status, ...extra });
      setExcFor(null);
      await reload();
    } catch (e) { toast.error("Could not update item"); }
  };

  const complete = async () => {
    try {
      await api.post(`/fx/pick/${order.order_number}/complete`);
      toast.success("Pick complete — sent to packing");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Some items still pending"); }
  };

  const resolved = order ? order.items.filter((i) => i.pick_status !== "pending").length : 0;

  return (
    <div data-testid="fx-picking">
      <ScanBar onSubmit={open} placeholder="Scan an order QR to start picking…" testid="fx-pick-scan" />
      {loading && <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>}
      {!loading && !order && <EmptyState>Scan or type an order number to load its pick sheet.</EmptyState>}
      {order && (
        <>
          <Card className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex-1">
              <p className="font-display text-2xl text-[#0D530E]">{order.order_number}</p>
              <p className="text-sm text-[#5C5C5C]">{order.customer.name} · {order.customer.city}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl text-[#306D29]">{resolved}/{order.items.length}</p>
              <p className="text-xs text-[#5C5C5C]">items handled</p>
            </div>
            <button onClick={() => printHtml(`Pick ${order.order_number}`, pickSheetHtml(order))} data-testid="fx-pick-print"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-black/10 text-[#306D29] hover:border-[#306D29]/40">
              <Printer size={14} /> Pick sheet
            </button>
          </Card>

          <div className="space-y-3">
            {order.items.map((it) => (
              <div key={it.key} className="bg-white border border-black/5 rounded-2xl p-4" data-testid={`fx-pick-item-${it.key}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-1">
                    <p className="font-medium">{it.name}</p>
                    <p className="text-xs text-[#5C5C5C]">Qty {it.quantity} · {it.source_store || "—"} · {it.category || ""}</p>
                    {it.note && <p className="text-xs text-[#D9933B] mt-1">Note: {it.note}</p>}
                  </div>
                  <PickPill status={it.pick_status} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {PICK_STATES.map((s) => (
                    <button key={s.id} data-testid={`fx-pick-${it.key}-${s.id}`}
                      onClick={() => EXCEPTION_STATES.includes(s.id) ? setExcFor({ key: it.key, status: s.id }) : mark(it.key, s.id, { qty_found: it.quantity })}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold ${it.pick_status === s.id ? s.tone : "bg-black/5 text-[#5C5C5C] hover:bg-black/10"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                {it.exception_note && <p className="text-xs text-[#B23B3B] mt-2">{it.exception_reason}: {it.exception_note}</p>}
              </div>
            ))}
          </div>

          <button onClick={complete} data-testid="fx-pick-complete"
            className="mt-6 w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2"
            style={{ transition: "background-color 0.2s ease" }}>
            <CheckCircle2 size={18} /> Mark pick complete
          </button>
        </>
      )}
      {excFor && <ExceptionModal excFor={excFor} onClose={() => setExcFor(null)} onSave={(extra) => mark(excFor.key, excFor.status, extra)} />}
    </div>
  );
}

function ExceptionModal({ excFor, onClose, onSave }) {
  const [reason, setReason] = useState(EXCEPTION_REASONS[0]);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.url);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="fx-exception-modal">
        <h3 className="font-display text-2xl text-[#0D530E] mb-4">Report exception</h3>
        <label className="text-sm font-semibold">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fx-exc-reason"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-3">
          {EXCEPTION_REASONS.map((r) => <option key={r}>{r}</option>)}
        </select>
        <label className="text-sm font-semibold">Note</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="fx-exc-note"
          className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-3" />
        <label className="flex items-center gap-2 text-sm text-[#306D29] cursor-pointer mb-4">
          <Camera size={16} /> {uploading ? "Uploading…" : photo ? "Photo attached ✓" : "Attach photo (optional)"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full border border-black/10 text-sm">Cancel</button>
          <button data-testid="fx-exc-save" onClick={() => onSave({ exception_reason: reason, exception_note: note, exception_photo: photo })}
            className="flex-1 py-2.5 rounded-full bg-[#B23B3B] text-white text-sm font-semibold">Save exception</button>
        </div>
      </div>
    </div>
  );
}
